import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import './AllocateFundPage.scss';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackwardIcon from '@mui/icons-material/ArrowBack';
import FundraiserCardV2 from '../../Common/FundraiserCardV2/FundraiserCardV2';
import dayjs from 'dayjs';
import { UserLoginContext } from '../../../../contexts/LoggedInUserContext';
import { allocateFunds, removeAllocatedFunds } from '../../../../redux/actions/AllocateFunds';
import { blitzTrigger, checkSipSubscribed } from '../../../../services/helperFunctions';
import { sendEvents } from '../../../../redux/actions/Home';
import { useLocation, useNavigate } from 'react-router-dom';
import EllipsisLoader from '../../EllipsisLoader';
import { useIsMobile } from '../../../hooks/useIsMobile';

const STATIC_AVATARS = [
  'https://randomuser.me/api/portraits/men/32.jpg',
  'https://randomuser.me/api/portraits/women/44.jpg',
  'https://randomuser.me/api/portraits/men/65.jpg',
];

const mapCampaignToCard = (campaign) => ({
  id: campaign?.id,
  title: campaign.title,
  gallery: campaign.gallery,
  beneficiary: campaign.beneficiary,
  days_left: (dayjs(campaign && campaign?.end_date)?.diff(new Date(), 'day') >= 0) ? dayjs(campaign && campaign?.end_date)?.diff(new Date(), 'day') : 0,
  donors_count: campaign.raised?.backers,
  amount_requested: Number(campaign.amount_requested),
  raised: { raised: Number(campaign.raised?.raised) },
  avatars: STATIC_AVATARS,
});

const AllocateFundPage = (props) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  // Get data from props or navigation state
  const allocateFundData = props.allocateFundData || location.state?.allocateFundData || [];
  const existingAllocatedFundList = props.existingAllocatedFundList || location.state?.existingAllocatedFundList || [];

  const { userData } = useContext(UserLoginContext);
  const cards = allocateFundData?.map(mapCampaignToCard);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const [existingAllocated, setExistingAllocated] = useState(0);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollStart = useRef(0);

  // POINTER EVENT HANDLERS
  const onPointerDown = e => {
    const slider = e.currentTarget;
    isDragging.current = true;
    slider.classList.add('dragging');
    startX.current = e.pageX - slider.offsetLeft;
    scrollStart.current = slider.scrollLeft;
    e.preventDefault();
  };

  const onPointerMove = e => {
    if (!isDragging.current) return;
    const slider = e.currentTarget;
    const x = e.pageX - slider.offsetLeft;
    const walk = (x - startX.current) * 1.5;  // tweak speed
    slider.scrollLeft = scrollStart.current - walk;
  };

  const onPointerUpOrLeave = e => {
    isDragging.current = false;
    e.currentTarget.classList.remove('dragging');
  };


  const handleGetAllocatedFunds = useCallback(() => {
    const list = existingAllocatedFundList?.map((item) => item?.campaign_id);
    let previousAllocateId = 0;
    const defaultSelectionId = allocateFundData?.[0]?.id;
    allocateFundData?.forEach((item) => {
      if (list?.includes(item?.id)) {
        previousAllocateId = item?.id;
        setExistingAllocated(previousAllocateId);
      }
    });
    setSelected(((list && list?.length === 0) || (previousAllocateId === 0)) ? defaultSelectionId : previousAllocateId);
  }, [allocateFundData, existingAllocatedFundList]);

  useEffect(() => {
    if (isMobile) window.scrollTo({ top: 0, behavior: 'smooth' });
    if (!allocateFundData.length) navigate(`/medical?allocate=true`, { replace: true });
    handleGetAllocatedFunds();
  }, []);

  const handleAllocate = async () => {
    const data = {
      campaignIds: [selected],
    };

    try {
      setIsLoading(true);
      if (existingAllocated) {
        const removeRes = await removeAllocatedFunds(userData?.id);
        if (removeRes.data.error === false) {
          await allocateFunds(userData?.id, data);
          sendEvents('funds_allocated', 'sip_returning', 'allocate_fundraiser', 'impact', selected, true);
          navigate(`/allocatefunds-thankyou`, { replace: isMobile });
          blitzTrigger('Allocate funds');
        }
      } else {
        await allocateFunds(userData?.id, data);
        sendEvents('funds_allocated', 'sip_returning', 'allocate_fundraiser', 'impact', selected, true);
        navigate(`/allocatefunds-thankyou`, { replace: isMobile });
        blitzTrigger('Allocate funds');
      }
    } catch (error) {
      console.error('Error allocating funds:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackCTA = ()=>{
    if (latestOrder?.campaign_cause) {
      navigate(`/${latestOrder.campaign_cause}`);
    } else {
      navigate('/sipselect');
    }
  }

  return (
    <>
      {isLoading && <div className='component-loader'><EllipsisLoader /></div>}
      {!!allocateFundData.length && <div className="afc-dialog-allocate">
        <div className="afc-dialog-title">
          <ArrowBackwardIcon className='mfs-20 back-btn' onClick={handleBackCTA} />
          Allocate Funds
        </div>
        <div className="afc-dialog-subtitle">
          <span>Choose where your money goes!</span>
          <span> You can select <b>1</b> of the below fundraiser to donate to your liking.</span>
        </div>
        <div className="afc-dialog-cards-wrapper" onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUpOrLeave}
          onPointerLeave={onPointerUpOrLeave}>
          {cards.map((data) => (
            <div
              key={data?.id}
              className={`afc-dialog-card-option${selected === data?.id ? ' selected' : ''}`}
              onClick={() => setSelected(data?.id)}
            >
              <FundraiserCardV2 campaignData={data} />
              <span className="afc-dialog-checkmark" onClick={() => setSelected(data?.id)}>
                {selected === data?.id ? (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#01BFBD" /><path d="M10 17L14 21L22 13" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                ) : (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="15.5" fill="white" stroke="#E0E0E0" /></svg>
                )}
              </span>
            </div>
          ))}
        </div>
        <div className='afc-dialog-allocate-btn-wrapper'>
          <button className="afc-dialog-allocate-btn" onClick={handleAllocate}>
            Allocate Funds <ArrowForwardIcon className='mfs-16 afc-dialog-arrow' />
          </button>
        </div>
      </div>}
    </>
  );
};

export default AllocateFundPage;